import React, { useState, useEffect, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef } from "ag-grid-community";
import { ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, Plus, Trash2, Eye, Edit, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { InfectionControlChecklist } from "../../../types/api";
import InfectionControlChecklistForm from "./InfectionControlChecklistForm";
import { INFECTION_CONTROL_SAFETY_PLANNING } from "../../../constants/modules";
import { usePermissions } from "../../../hooks/usePermission";
import {
  useInfectionControlStore,
  useProjectUtilityStore,
} from "../../../store/utilitySystemsStore";
import { useLocationStore } from "../../../store/masterDataStore";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const InfectionControlChecklistList: React.FC = () => {
  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] =
    useState<InfectionControlChecklist | null>(null);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [approvalFilter, setApprovalFilter] = useState<string>("");
  const gridRef = useRef<AgGridReact>(null);

  const {
    checklists,
    checklistsLoading,
    fetchChecklists,
    deleteChecklist,
    approveChecklist,
  } = useInfectionControlStore();
  const { projects, fetchProjects } = useProjectUtilityStore();
  const { allLocationsMap, fetchAllLocations } = useLocationStore();

  const fetchChecklistsData = useCallback(async () => {
    try {
      if (!projects.length) await fetchProjects();
      if (!allLocationsMap.size) await fetchAllLocations();

      const params: any = {};
      if (projectFilter) params.projectId = projectFilter;
      if (approvalFilter !== "") params.isApproved = approvalFilter === "true";

      await fetchChecklists(params);
    } catch (error: any) {
      toast.error("Failed to fetch checklists");
      console.error("Error fetching checklists:", error);
    }
  }, [
    fetchChecklists,
    projectFilter,
    approvalFilter,
    projects.length,
    fetchProjects,
    allLocationsMap.size,
    fetchAllLocations,
  ]);

  useEffect(() => {
    fetchChecklistsData();
  }, [fetchChecklistsData]);

  const handleCreate = () => {
    setSelectedChecklist(null);
    setMode("create");
    setIsFormOpen(true);
  };

  const handleEdit = (checklist: InfectionControlChecklist) => {
    setSelectedChecklist(checklist);
    setMode("edit");
    setIsFormOpen(true);
  };

  const handleView = (checklist: InfectionControlChecklist) => {
    setSelectedChecklist(checklist);
    setMode("view");
    setIsFormOpen(true);
  };

  const handleDelete = async (checklist: InfectionControlChecklist) => {
    if (!window.confirm("Are you sure you want to delete this checklist?")) {
      return;
    }

    try {
      const id = checklist.id || checklist._id;
      if (!id) return;

      await deleteChecklist(id);
      toast.success("Checklist deleted successfully");
      fetchChecklistsData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to delete checklist");
      console.error("Error deleting checklist:", error);
    }
  };

  const handleApprove = async (checklist: InfectionControlChecklist) => {
    if (!window.confirm("Approve this infection control checklist?")) {
      return;
    }

    try {
      const id = checklist.id || checklist._id;
      if (!id) return;

      await approveChecklist(id);
      toast.success("Checklist approved successfully");
      fetchChecklistsData();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to approve checklist");
      console.error("Error approving checklist:", error);
    }
  };

  const filteredChecklists = checklists.filter(
    (checklist: InfectionControlChecklist) => {
      const matchesSearch =
        !searchTerm ||
        checklist.locationName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        projects
          .find((p) => (p.id || p._id) === checklist.projectId)
          ?.name?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    },
  );

  const columnDefs: ColDef[] = [
    {
      headerName: "Project",
      field: "projectId",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      // cellStyle: { padding: "10px 0 0 10px" },
      cellRenderer: (params: any) => {
        const project = projects.find(
          (p: any) => (p.id || p._id) === params.value,
        );
        return project?.name || "N/A";
      },
    },
    {
      headerName: "Location",
      field: "locationName",
      flex: 1,
    },
    {
      headerName: "Patient Sensitive",
      field: "isPatientSensitiveArea",
      flex: 1,
      cellRenderer: (params: any) => {
        return params.value ? (
          <span className="text-red-600 font-semibold">Yes</span>
        ) : (
          <span className="text-gray-600">No</span>
        );
      },
    },
    {
      headerName: "Items Checked",
      field: "checklistItems",
      flex: 1,
      cellRenderer: (params: any) => {
        const items = params.value || [];
        const checkedCount = items.filter((item: any) => item.checked).length;
        return `${checkedCount}/${items.length}`;
      },
    },
    {
      headerName: "Status",
      field: "isApproved",
      flex: 1,
      cellRenderer: (params: any) => {
        return params.value ? (
          <span className="text-green-600 font-semibold">Approved</span>
        ) : (
          <span className="text-yellow-600 font-semibold">Pending</span>
        );
      },
    },
    {
      headerName: "Approved By",
      field: "approverName",
      flex: 1,
      cellRenderer: (params: any) => {
        return params.value || "N/A";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      cellRenderer: (params: any) => {
        const checklist: InfectionControlChecklist = params.data;
        return (
          <div className="flex justify-between gap-2 items-center h-60%">
            <button
              onClick={() => handleView(checklist)}
              className="p-0 text-blue-600 hover:bg-blue-50 rounded"
              title="View"
            >
              <Eye size={16} />
            </button>
            {canUpdate(INFECTION_CONTROL_SAFETY_PLANNING) &&
              !checklist.isApproved && (
                <>
                  <button
                    onClick={() => handleEdit(checklist)}
                    className="p-0 text-green-600 hover:bg-green-50 rounded"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleApprove(checklist)}
                    className="p-0 text-purple-600 hover:bg-purple-50 rounded"
                    title="Approve"
                  >
                    <CheckCircle size={16} />
                  </button>
                </>
              )}
            {canDelete(INFECTION_CONTROL_SAFETY_PLANNING) && (
              <button
                onClick={() => handleDelete(checklist)}
                className="p-0 text-red-600 hover:bg-red-50 rounded"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-2 py-4 bg-gray-50 min-h-screen mt-14">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-800">
            Infection Control & Safety Planning
          </h1>
          {canCreate(INFECTION_CONTROL_SAFETY_PLANNING) && (
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            >
              <Plus size={20} />
              New Checklist
            </button>
          )}
        </div>

        <div className="mb-6 flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by project or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
              />
            </div>
          </div>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
          >
            <option value="">All Projects</option>
            {projects.map((project: any) => (
              <option
                key={project.id || project._id}
                value={project.id || project._id}
              >
                {project.name}
              </option>
            ))}
          </select>
          <select
            value={approvalFilter}
            onChange={(e) => setApprovalFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="false">Pending</option>
            <option value="true">Approved</option>
          </select>
        </div>

        <div
          className="ag-theme-alpine"
          style={{ height: "600px", width: "100%" }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={filteredChecklists}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              resizable: true,
            }}
            pagination={true}
            paginationPageSize={10}
            loading={checklistsLoading}
            rowHeight={50}
            headerHeight={65}
            getRowStyle={() => {
              return {
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                marginBottom: "8px",
                borderRadius: "4px",
                backgroundColor: "white",
                height: "60px",
                padding: "10px 0 0 5px",
                alignItems: "center",
              };
            }}
          />
        </div>
      </div>

      <InfectionControlChecklistForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedChecklist(null);
        }}
        checklist={selectedChecklist}
        mode={mode}
        onSuccess={() => {
          fetchChecklistsData();
          setIsFormOpen(false);
        }}
      />
    </div>
  );
};

export default InfectionControlChecklistList;
